import React, { useState, useRef, useCallback, useEffect } from 'react';
import { X, ChevronDown, ChevronRight, Code, Database, Globe, FolderOpen, FlaskConical, FileText, GripVertical, Key, Bot } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface CollapsibleSectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

function CollapsibleSection({ title, icon, children, defaultOpen = false }: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border-b last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 w-full px-3 py-2 hover:bg-muted/50 transition-colors text-left"
      >
        {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        {icon}
        <span className="font-medium text-sm">{title}</span>
      </button>
      {isOpen && (
        <div className="px-3 pb-3 text-sm">
          {children}
        </div>
      )}
    </div>
  );
}

function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="bg-muted p-2 rounded text-xs font-mono overflow-x-auto whitespace-pre-wrap">
      {children}
    </pre>
  );
}

interface NotebookHelpPanelProps {
  onClose: () => void;
}

export default function NotebookHelpPanel({ onClose }: NotebookHelpPanelProps) {
  const [width, setWidth] = useState(320);
  const [isResizing, setIsResizing] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const MIN_WIDTH = 280;
  const MAX_WIDTH = 600;

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing || !panelRef.current) return;

      const panelRect = panelRef.current.getBoundingClientRect();
      const newWidth = panelRect.right - e.clientX;
      setWidth(Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth)));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  return (
    <div
      ref={panelRef}
      style={{ width }}
      className="border-l bg-background flex flex-col relative shrink-0 self-stretch"
    >
      {/* Resize handle */}
      <div
        onMouseDown={handleMouseDown}
        className={cn(
          "absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/20 transition-colors z-10",
          "flex items-center justify-center",
          isResizing && "bg-primary/30"
        )}
      >
        <GripVertical className="w-3 h-3 text-muted-foreground opacity-0 hover:opacity-100 transition-opacity" />
      </div>

      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b shrink-0">
        <h3 className="font-semibold text-sm">Python API Reference</h3>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Content - scrollable area */}
      <div className="flex-1 overflow-y-auto min-h-0 flex flex-col">
        <div className="flex-1">
        {/* Keyboard shortcuts */}
        <CollapsibleSection title="Keyboard Shortcuts" icon={<Code className="w-4 h-4" />} defaultOpen>
          <div className="space-y-1 text-muted-foreground">
            <div className="flex justify-between">
              <span>Run cell</span>
              <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">Shift+Enter</kbd>
            </div>
            <div className="flex justify-between">
              <span>Add code cell</span>
              <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">B</kbd>
            </div>
            <div className="flex justify-between">
              <span>Add markdown cell</span>
              <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">M</kbd>
            </div>
            <div className="flex justify-between">
              <span>Edit cell</span>
              <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">Double-click</kbd>
            </div>
            <div className="flex justify-between">
              <span>Exit edit / Save</span>
              <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">Escape</kbd>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Cells auto-save when you click away or press Escape. Markdown cells render when not editing.
          </p>
        </CollapsibleSection>

        {/* Available Libraries */}
        <CollapsibleSection title="Available Libraries" icon={<Code className="w-4 h-4" />} defaultOpen>
          <div className="space-y-3">
            <div>
              <p className="text-muted-foreground mb-1 font-medium">Data Science & ML:</p>
              <CodeBlock>{`import numpy as np
import pandas as pd
import scipy
from sklearn.ensemble import RandomForestClassifier
import statsmodels.api as sm
import xgboost as xgb
import lightgbm as lgb`}</CodeBlock>
            </div>
            <div>
              <p className="text-muted-foreground mb-1 font-medium">Visualization:</p>
              <CodeBlock>{`import matplotlib.pyplot as plt
from bokeh.plotting import figure`}</CodeBlock>
            </div>
            <div>
              <p className="text-muted-foreground mb-1 font-medium">Data & Finance:</p>
              <CodeBlock>{`import pyxirr  # IRR/NPV calculations
import orjson  # Fast JSON
from bs4 import BeautifulSoup
import networkx as nx
import fastparquet`}</CodeBlock>
            </div>
            <div>
              <p className="text-muted-foreground mb-1 font-medium">Utilities:</p>
              <CodeBlock>{`import json, re, math, datetime
from pydantic import BaseModel
from dateutil import parser
import jsonschema`}</CodeBlock>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Heavy packages (numpy, pandas, scipy, sklearn, etc.) are lazy-loaded on first import.
              Python variables persist across cells. Use <code>cap.storage</code> to persist between sessions.
            </p>
          </div>
        </CollapsibleSection>

        {/* Storage API */}
        <CollapsibleSection title="cap.storage" icon={<Database className="w-4 h-4" />}>
          <div className="space-y-2">
            <p className="text-muted-foreground mb-2">Key-value storage (100KB max per value):</p>
            <CodeBlock>{`# Get a value
value = await cap.storage.get("my_key")

# Set a value (JSON-serializable)
await cap.storage.set("my_key", {"data": 123})

# Delete a value
await cap.storage.delete("my_key")

# List keys with prefix
keys = await cap.storage.keys("prefix_")`}</CodeBlock>
            <p className="text-xs text-muted-foreground mt-2">
              Storage is scoped to your team and notebook.
            </p>
          </div>
        </CollapsibleSection>

        {/* Secrets API */}
        <CollapsibleSection title="cap.secrets" icon={<Key className="w-4 h-4" />}>
          <div className="space-y-2">
            <p className="text-muted-foreground mb-2">Access team secrets (whitelisted):</p>
            <CodeBlock>{`# Get a secret value
api_key = await cap.secrets.get("OPENAI_KEY")

# List available secrets (names only)
available = cap.secrets.list()
# Returns: ["OPENAI_KEY", "POLYGON_KEY"]`}</CodeBlock>
            <p className="text-xs text-muted-foreground mt-2">
              Requires <code>secrets: {"{allowed: [\"SECRET_NAME\"]}"}</code> capability.
              Only whitelisted secrets can be accessed.
            </p>
          </div>
        </CollapsibleSection>

        {/* Fetch API */}
        <CollapsibleSection title="cap.fetch" icon={<Globe className="w-4 h-4" />}>
          <div className="space-y-2">
            <p className="text-muted-foreground mb-2">HTTP requests to whitelisted domains:</p>
            <CodeBlock>{`# GET request
response = await cap.fetch(
    "https://api.polygon.io/v2/aggs/ticker/AAPL/prev"
)
data = response.json()

# POST request
response = await cap.fetch(
    "https://httpbin.org/post",
    method="POST",
    headers={"Content-Type": "application/json"},
    body=json.dumps({"key": "value"})
)`}</CodeBlock>
            <p className="text-xs text-muted-foreground mt-2">
              <strong>Allowed domains:</strong> api.polygon.io, api.openai.com, api.anthropic.com, api.github.com, httpbin.org
            </p>
          </div>
        </CollapsibleSection>

        {/* Trajectory API */}
        <CollapsibleSection title="cap.trajectory" icon={<FolderOpen className="w-4 h-4" />}>
          <div className="space-y-2">
            <p className="text-muted-foreground mb-2">Read trajectory events and metadata:</p>
            <CodeBlock>{`# Get trajectory events
events = await cap.trajectory.getEvents(
    limit=50,
    offset=0,
    event_type="tool_call"  # optional filter
)

# Get trajectory metadata
metadata = await cap.trajectory.getMetadata()

# Get current trajectory ID
tid = cap.trajectory.getId()`}</CodeBlock>
            <p className="text-xs text-muted-foreground mt-2">
              Only available when running in eval context with a trajectory.
            </p>
          </div>
        </CollapsibleSection>

        {/* Data Files API */}
        <CollapsibleSection title="cap.data" icon={<FolderOpen className="w-4 h-4" />}>
          <div className="space-y-2">
            <p className="text-muted-foreground mb-2">Access uploaded data files:</p>
            <CodeBlock>{`# List available files
files = cap.data.list()
# Returns: [{id, alias, name, file_type, file_size}]

# Get file content by alias or ID
file = await cap.data.get("my_file_alias")
content = file["content"]
file_type = file["file_type"]`}</CodeBlock>
          </div>
        </CollapsibleSection>

        {/* Evals API */}
        <CollapsibleSection title="cap.evals" icon={<FlaskConical className="w-4 h-4" />}>
          <div className="space-y-2">
            <p className="text-muted-foreground mb-2">Access and run evals:</p>
            <CodeBlock>{`# Start an eval run
run = await cap.evals.run("eval_id", {
    "variant_ids": ["variant-1", "variant-2"]
})
# Returns: {eval_run_id, trajectory_id, status}

# List eval runs for your team
runs = await cap.evals.listRuns(
    limit=20,
    status="completed"  # optional
)

# Get eval run details
run = await cap.evals.getRun("run_id")

# Get trial results
trials = await cap.evals.getTrials("run_id")

# Get aggregate scores
scores = await cap.evals.getScores("run_id")

# List eval definitions
defs = await cap.evals.listDefinitions()`}</CodeBlock>
            <p className="text-xs text-muted-foreground mt-2">
              Requires <code>evals: {"{read: true}"}</code> for reads,
              <code>evals: {"{write: true}"}</code> for running evals.
            </p>
          </div>
        </CollapsibleSection>

        {/* Content Store API */}
        <CollapsibleSection title="cap.content" icon={<FileText className="w-4 h-4" />}>
          <div className="space-y-2">
            <p className="text-muted-foreground mb-2">Access and manage agents, prompts, and content:</p>
            <CodeBlock>{`# List agents
agents = await cap.content.list(
    content_type="agent"
)

# Get content by ID
agent = await cap.content.get("item_id")

# Get content by path
agent = await cap.content.getByPath(
    "folder/my-agent",
    "agent"
)

# List folders
folders = await cap.content.listFolders()

# Create new content
item = await cap.content.create({
    "name": "my-agent",
    "content_type": "agent",
    "content": "---\\nmodel: openai:gpt-4\\n---\\n...",
    "folder_id": "folder-uuid",  # optional
    "publish": True
})

# Update existing content
await cap.content.update("item_id", {
    "content": "updated content...",
    "publish": True
})`}</CodeBlock>
            <p className="text-xs text-muted-foreground mt-2">
              Requires <code>content: {"{read: true}"}</code> for reads,
              <code>content: {"{write: true}"}</code> for create/update.
            </p>
          </div>
        </CollapsibleSection>

        {/* Agents API */}
        <CollapsibleSection title="cap.agents" icon={<Bot className="w-4 h-4" />}>
          <div className="space-y-2">
            <p className="text-muted-foreground mb-2">Run agents asynchronously:</p>
            <CodeBlock>{`# Start an agent run
job = await cap.agents.run("agent-id-or-path", {
    "input_data": {"ticker": "AAPL"},
    "messages": [{"role": "user", "content": "Analyze"}]
})
# Returns: {trajectory_id, status, agent_name}

# Check status
status = await cap.agents.status(job["trajectory_id"])
# Returns: {status, events_count, started_at, ...}

# Get output when done
result = await cap.agents.getOutput(job["trajectory_id"])
# Returns: {final_output, tool_calls, metadata}

# Cancel a running job
await cap.agents.cancel(job["trajectory_id"])`}</CodeBlock>
            <p className="text-xs text-muted-foreground mt-2">
              Requires <code>agents: {"{run: true}"}</code> capability.
              Agents run asynchronously - poll status until complete.
            </p>
          </div>
        </CollapsibleSection>

        {/* Common Patterns */}
        <CollapsibleSection title="Common Patterns" icon={<Code className="w-4 h-4" />}>
          <div className="space-y-3">
            <div>
              <p className="text-muted-foreground mb-1 font-medium">Load CSV from storage:</p>
              <CodeBlock>{`import pandas as pd
import io

csv_str = await cap.storage.get("my_data")
df = pd.read_csv(io.StringIO(csv_str))`}</CodeBlock>
            </div>
            <div>
              <p className="text-muted-foreground mb-1 font-medium">Fetch and parse JSON API:</p>
              <CodeBlock>{`response = await cap.fetch(
    "https://api.polygon.io/v2/aggs/ticker/AAPL/prev"
)
if response.ok:
    data = response.json()
    print(data["results"][0]["c"])  # closing price`}</CodeBlock>
            </div>
            <div>
              <p className="text-muted-foreground mb-1 font-medium">DataFrame operations:</p>
              <CodeBlock>{`import pandas as pd
import numpy as np

# Create DataFrame
df = pd.DataFrame({
    "a": [1, 2, 3],
    "b": [4, 5, 6]
})

# Basic stats
df.describe()

# Filter
df[df["a"] > 1]

# Group and aggregate
df.groupby("a").mean()`}</CodeBlock>
            </div>
            <div>
              <p className="text-muted-foreground mb-1 font-medium">Use matplotlib (inline display):</p>
              <CodeBlock>{`import matplotlib
matplotlib.use('agg')  # Required in Pyodide
import matplotlib.pyplot as plt
import numpy as np

x = np.linspace(0, 10, 100)
plt.figure(figsize=(8, 4))
plt.plot(x, np.sin(x), label='sin(x)')
plt.plot(x, np.cos(x), label='cos(x)')
plt.title("Sine and Cosine Waves")
plt.legend()
plt.show()  # Chart displays inline!`}</CodeBlock>
              <p className="text-xs text-muted-foreground mt-1">
                Note: Use <code>matplotlib.use('agg')</code> before importing pyplot.
                Charts display inline automatically after cell execution.
              </p>
            </div>
          </div>
        </CollapsibleSection>
        </div>

        {/* Capabilities Note - sticks to bottom of scrollable area */}
        <div className="px-3 py-2 text-xs text-muted-foreground border-t bg-muted/30 shrink-0 mt-auto">
          <p>
            <strong>Note:</strong> Capabilities (storage, secrets, fetch, trajectory, data, evals, content, agents) must be enabled
            in notebook settings. Code runs in a sandboxed Pyodide environment.
          </p>
        </div>
      </div>
    </div>
  );
}
